<template>
  <div
    ref="grid"
    class="card-grid"
    :style="styles"
  >
    <BaseIconCard
      v-for="(card, inx) in items"
      :key="inx"
      :style="{
        'grid-column': card.span ? `span ${card.span > currentCols ? currentCols : card.span}` : 'auto',
      }"
      v-bind="card"
      :default-modal="defaultModal"
      @update:vars="vars[card.name] = $event"
    >
      <template #action="{ action, toggleModal }">
        <slot
          name="action"
          :action="action"
          :toggle-modal="toggleModal"
        />
      </template>
      <template #actions>
        <slot
          :name="`${card.name}_actions`"
          v-bind="{ card, ...vars[card.name]}"
        />
      </template>
      <template #default>
        <slot v-bind="{ card, ...vars[card.name]}" />
      </template>
    </BaseIconCard>
  </div>
</template>

<script>
export default {
  name: 'CardGrid',
  props: {
    items: {
      type: Array,
      required: true,
    },
    minWidth: {
      type: Number,
      default: 450,
    },
    maxWidth: {
      type: Number,
      default: null,
    },
    gap: {
      type: Number,
      default: 2,
    },
    cols: {
      type: Number,
      default: null,
    },
    maxCols: {
      type: Number,
      default: null,
    },
    defaultModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      vars: this.items.reduce((acc, item) => {
        acc[item.name] = {};
        return acc;
      }, {}),
      currentCols: this.cols || 'auto-fit',
    };
  },
  computed: {
    styles() {
      return {
        '--min-width': `${this.minWidth}px`,
        '--max-width': this.maxWidth ? `${this.maxWidth}px` : '1fr',
        '--gap': `${this.gap}rem`,
        '--cols': 'auto-fit',
        '--max-col-width': `${this.maxColWidth}px`,
      };
    },
    maxColWidth() {
      return (this.maxCols + 1) * this.minWidth + this.maxCols * (this.gap * 16);
    },
    colWidthList() {
      const fixedCols = this.cols || this.items.length;
      const cols = [];
      for (let i = 1; i <= fixedCols; i += 1) {
        cols.push(this.minWidth * i + (i - 1) * (this.gap * 16));
      }
      return cols;
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.setCols();
    });
    window.addEventListener('resize', this.setCols);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.setCols);
  },
  methods: {
    setCols() {
      if (this.maxCols) {
        if (this.$refs.grid.offsetWidth > this.maxColWidth && this.maxCols <= this.items.length) {
          this.$refs.grid.style.setProperty('--cols', this.maxCols);
          this.currentCols = this.maxCols;
        } else {
          this.$refs.grid.style.setProperty('--cols', 'auto-fit');
          this.currentCols = 'auto-fit';
        }
      }
      if (this.cols) {
        const { cols } = this;
        const colWidth = this.colWidthList.find((width) => width > this.$refs.grid.offsetWidth);

        if (colWidth) {
          this.$refs.grid.style.setProperty('--cols', this.colWidthList.indexOf(colWidth));
          this.currentCols = this.colWidthList.indexOf(colWidth);
        } else {
          this.$refs.grid.style.setProperty('--cols', cols);
          this.currentCols = cols;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.card-grid {
  display: grid;
  grid-template-columns: repeat(var(--cols), minmax(var(--min-width), var(--max-width)));
  grid-gap: var(--gap);
}

.base-form {
  align-self: baseline;
}

@media screen and (max-width: 500px) {
  .card-grid {
    grid-template-columns: repeat(auto-fit, minmax(80%, 1fr));
  }
}

</style>
